
import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Phone, Clock } from 'lucide-react';

interface CallHistoryItemProps {
  call: {
    id: string;
    number: string;
    name?: string;
    timestamp: Date;
    duration: string;
    outcome: string;
    direction: string;
    quality?: string;
    notes?: string;
    call_price?: number;
    call_price_unit?: string;
  };
}

export const CallHistoryItem = ({ call }: CallHistoryItemProps) => {
  const getOutcomeBadge = (outcome: string) => {
    switch (outcome) {
      case 'connected':
        return <Badge variant="default" className="bg-green-500">Connected</Badge>;
      case 'voicemail':
        return <Badge variant="secondary">Voicemail</Badge>;
      case 'busy':
        return <Badge variant="destructive">Busy</Badge>;
      case 'no-answer':
        return <Badge variant="outline">No Answer</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">{outcome}</Badge>;
    }
  };

  const getQualityColor = (quality?: string) => {
    switch (quality) {
      case 'excellent': return 'bg-green-500';
      case 'good': return 'bg-blue-500';
      case 'poor': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const formatTimeAgo = (timestamp: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - timestamp.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else {
      return `${diffDays}d ago`;
    }
  };

  const formatDateTime = (timestamp: Date) => {
    const now = new Date();
    const isToday = now.toDateString() === timestamp.toDateString();
    const isYesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString() === timestamp.toDateString();
    
    let dateStr;
    if (isToday) {
      dateStr = 'Today';
    } else if (isYesterday) {
      dateStr = 'Yesterday';
    } else {
      dateStr = timestamp.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: timestamp.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
    
    const timeStr = timestamp.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    return { date: dateStr, time: timeStr };
  };

  const dateTime = formatDateTime(call.timestamp);

  return (
    <Card className="p-3 hover:bg-muted/50 transition-colors">
      <div className="space-y-2">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Phone className={`h-4 w-4 ${
                call.direction === 'inbound' 
                  ? 'text-blue-500 rotate-180' 
                  : 'text-green-500'
              }`} />
              <span className="font-medium text-sm">
                {call.name || call.number}
              </span>
            </div>
            
            {call.name && (
              <div className="text-xs text-muted-foreground mb-1">
                {call.number}
              </div>
            )}
            
            {/* Date and Time Display */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <span>{dateTime.date}</span>
              <span>•</span>
              <span>{dateTime.time}</span>
            </div>
          </div>
          
          <div className="text-right">
            <div className="text-xs text-muted-foreground">
              {formatTimeAgo(call.timestamp)}
            </div>
            {call.quality && (
              <div className="flex items-center gap-1 mt-1 justify-end">
                <div className={`w-2 h-2 rounded-full ${getQualityColor(call.quality)}`} />
                <span className="text-xs capitalize">{call.quality}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getOutcomeBadge(call.outcome)}
            <Badge variant="outline" className="text-xs">
              <Clock className="h-3 w-3 mr-1" />
              {call.duration}
            </Badge>
            {(call.call_price !== null && call.call_price !== undefined) && (
              <Badge variant="outline" className="text-xs text-purple-600 border-purple-200 dark:border-purple-800">
                ${Math.abs(call.call_price).toFixed(3)}
              </Badge>
            )}
          </div>
        </div>
        
        {call.notes && (
          <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
            {call.notes}
          </div>
        )}
      </div>
    </Card>
  );
};
